import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { company } from '../../mock';
import { useContent } from '../../content';

const Introduction = () => {
  const { introduction: intro, stats } = useContent();
  const [g, setG] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setG((p) => (p + 1) % intro.gallery.length), 4000);
    return () => clearInterval(t);
  }, [intro.gallery.length]);

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(company.mapQuery)}&z=15&output=embed`;

  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
      {/* Left: map + gallery */}
      <div className="space-y-6 order-2 lg:order-1">
        <div className="relative aspect-[4/3] w-full rounded-2xl border border-white/10 overflow-hidden bg-navy">
          <iframe
            title="Intervantis HQ map"
            src={mapSrc}
            className="w-full h-full [filter:grayscale(0.15)] dark:[filter:invert(0.9)_hue-rotate(180deg)_brightness(0.92)_contrast(0.9)_saturate(0.85)]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <div className="relative aspect-[16/10] w-full rounded-2xl overflow-hidden border border-white/10">
          {intro.gallery.map((im, i) => (
            <img
              key={im.src}
              src={im.src}
              alt={im.tag}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === g ? 'opacity-100' : 'opacity-0'}`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
          <div className="absolute bottom-4 left-4 flex items-center gap-3">
            <span className="label-mono text-white/90 bg-navy/60 backdrop-blur px-3 py-1.5 border border-white/10">
              {intro.gallery[g].tag}
            </span>
          </div>
          <div className="absolute bottom-5 right-4 flex gap-1.5">
            {intro.gallery.map((_, i) => (
              <button
                key={i}
                onClick={() => setG(i)}
                className={`h-1.5 transition-all ${i === g ? 'w-6 bg-ember' : 'w-1.5 bg-white/40'}`}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        </div>
        <p className="text-white/50 font-body text-sm leading-relaxed">{intro.galleryCaption}</p>
      </div>

      {/* Right: text */}
      <div className="order-1 lg:order-2">
        <div className="label-mono text-ember mb-6">[{intro.index}] · {intro.label}</div>
        <h2 className="font-display font-light text-white text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-tight">
          {intro.headingLead}
          <span className="text-ember">{intro.headingAccent}</span>
        </h2>
        <div className="mt-8 space-y-5">
          {intro.paragraphs.map((p, i) => (
            <p key={i} className="text-white/75 font-body text-base md:text-[17px] leading-relaxed">{p}</p>
          ))}
        </div>
        <blockquote className="mt-8 border-l-2 border-ember pl-5 text-white/90 font-body italic text-base md:text-lg leading-relaxed">
          {intro.quote}
        </blockquote>
        <Link
          to="/meet-the-team"
          className="group mt-8 inline-flex items-center gap-3 label-mono text-white hover:text-ember transition-colors"
        >
          <span className="w-8 h-px bg-ember" />
          {intro.cta}
          <ArrowUpRight size={16} className="text-ember transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>

        {/* Key figures — fills the column and reinforces credibility */}
        <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
          {stats.map((s, i) => (
            <div key={i} className="bg-navy/60 p-6 md:p-7">
              <div className="font-display font-bold text-ember text-3xl md:text-4xl leading-none">{s.value}</div>
              <div className="mt-3 text-white/60 font-body text-sm leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Introduction;
